import { useEffect, useState } from "react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar, Users, MessageCircle, Tag, Phone, Mail } from "lucide-react";

function digits(s) { return (s || "").replace(/\D/g, ""); }

export default function HostInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [price, setPrice] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/host/inquiries"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendOffer = async () => {
    if (!price) return toast.error("Set a price");
    try {
      await api.post(`/host/inquiries/${active.id}/offer`, {
        custom_price_per_night: Number(price), message: msg, expires_in_hours: 48,
      });
      toast.success("Offer sent to traveler!");
      setActive(null); setPrice(""); setMsg("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-display font-bold mb-2">Inquiries</h1>
        <p className="text-[#717171] mb-8">Travelers waiting for your custom price</p>

        {loading ? <p className="text-[#717171]">Loading...</p> : items.length === 0 ? (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-12 text-center text-[#717171]">
            No inquiries yet. Once travelers request custom prices, they'll show up here.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((q) => {
              const wa = digits(q.traveler_phone);
              return (
                <div key={q.id} data-testid={`host-inquiry-${q.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
                  <div className="flex flex-wrap items-center gap-3 mb-3 justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="font-display font-semibold text-lg">{q.hotel_name}</h3>
                      <Badge className={
                        q.status === "pending" ? "bg-[#FFB400]/15 text-[#A07000] border-0" :
                        q.status === "offered" ? "bg-[#007A87]/15 text-[#007A87] border-0" :
                        q.status === "accepted" ? "bg-[#008A05]/10 text-[#008A05] border-0" :
                        "bg-[#F0F0F4] text-[#555] border-0"
                      }>{q.status}</Badge>
                    </div>
                    <div className="flex gap-2">
                      {wa && (
                        <a href={`https://wa.me/${wa}?text=Hi%20${encodeURIComponent(q.traveler_name)}!`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-xs font-semibold">
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                      <a href={`mailto:${q.traveler_email}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-[#DDD] text-xs font-semibold">
                        <Mail className="w-3 h-3" /> Email
                      </a>
                    </div>
                  </div>
                  <div className="text-sm text-[#444] flex flex-wrap gap-4 mb-3">
                    <span>From <strong>{q.traveler_name}</strong></span>
                    {q.contact_locked && (
                      <a href="/host/subscription" className="px-2 py-0.5 rounded-full bg-[#FFB400]/15 text-[#A07000] text-xs font-semibold">🔒 Subscribe to reveal contact</a>
                    )}
                    {q.traveler_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {q.traveler_phone}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {q.check_in} → {q.check_out} · {q.nights}n</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {q.guests} guests</span>
                    {q.room_name && <span>· {q.room_name}</span>}
                  </div>
                  {q.message && (
                    <div className="bg-[#F7F7F9] rounded-xl p-3 text-sm text-[#444] mb-3">"{q.message}"</div>
                  )}
                  {q.offer ? (
                    <div className="border-t border-[#EBEBEB] pt-3 text-sm">
                      <p className="text-xs uppercase tracking-wide text-[#717171] mb-1 flex items-center gap-1"><Tag className="w-3 h-3" /> Your offer</p>
                      <p><strong>{inr(q.offer.custom_price_per_night)}</strong> / night × {q.offer.nights} = <strong>{inr(q.offer.total_price)}</strong> (incl. {inr(q.offer.platform_fee)} fee). Status: <strong>{q.offer.status}</strong></p>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <Dialog open={active?.id === q.id} onOpenChange={(o) => !o && setActive(null)}>
                        <DialogTrigger asChild>
                          <Button data-testid={`send-offer-btn-${q.id}`} onClick={() => setActive(q)} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">
                            <Tag className="w-4 h-4 mr-1" /> Send price offer
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader><DialogTitle>Send custom offer</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs mb-1 block">Price per night (INR)</Label>
                              <Input data-testid="offer-price-input" type="number" placeholder="e.g. 3500" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-xl" />
                              {price && active && (
                                <p className="text-xs text-[#717171] mt-1">
                                  Total: <strong>{inr(Number(price) * active.nights + 299)}</strong> ({active.nights} nights + ₹299 platform fee)
                                </p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Message (optional)</Label>
                              <Textarea data-testid="offer-message-input" rows={3} placeholder="Hi, here's our best price..." value={msg} onChange={(e) => setMsg(e.target.value)} className="rounded-xl" />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button data-testid="confirm-send-offer-btn" onClick={sendOffer} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white w-full">
                              Send offer to traveler
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
